import React, { useCallback, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useErrorHandling } from '../hooks/useErrorHandling';
import { createTimezonedDate } from '../util/date';
import { EditButtonsSet } from '../util';
import generalRouteService from '../services/general_route.service';
import FolderFileIcon from './FolderFileIcon';
import { SHOW_EDIT_BUTTON_FOR_RESTRICTED_FOLDER } from '@rd-web-markets/market/dist/constants';
import { FILE_MANAGEMENT_COLUMN_ORDER } from '@rd-web-markets/market/dist/constants';
import ongoingTemplateImportService from '@services/claim_groups/ongoing_template_import.service';
import projectTemplateImportService from '@services/claim_groups/project_template_import.service';
const fileShortcutModelNames = ['BeSection::OngoingTemplateImport', 'BeSection::ProjectTemplateImport'];
const apiFilePathsToShortcutFiles = {
  'BeSection::OngoingTemplateImport': {
    service: ongoingTemplateImportService,
    routeIds: ['parent_of_parent_id', 'parent_attachment_model_id']
  },
  'BeSection::ProjectTemplateImport': {
    service: projectTemplateImportService,
    routeIds: ['parent_of_parent_id', 'parent_attachment_model_id']
  }
};
const FoldersAndFilesRow = _ref => {
  let {
    data,
    route,
    setAllData,
    allData,
    setCurrentFolderId,
    goToFolderQuery,
    canEditExistingDocumentsOrFolders = true
  } = _ref;
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [body, setBody] = useState({
    ...data
  });
  const showEditIfRestricted = body.restricted ? SHOW_EDIT_BUTTON_FOR_RESTRICTED_FOLDER : true;
  const changeBody = event => {
    const value = event.target.value;
    setBody({
      ...body,
      [event.target.name]: value
    });
  };
  const deleteData = useErrorHandling(useCallback(async () => {
    if (window.confirm(`Would you like to delete "${body.name, data.file_parent_class_name}"?`)) {
      if (fileShortcutModelNames.includes(data.file_parent_class_name)) {
        const service = apiFilePathsToShortcutFiles[data.file_parent_class_name].service;
        const routeIds = apiFilePathsToShortcutFiles[data.file_parent_class_name].routeIds.map(r => data[r]);
        await service.delete(...routeIds);
      } else {
        await generalRouteService.delete(route, data.id);
      }
      const indexToRemove = allData.findIndex(currentData => currentData.id === data.id);
      allData.splice(indexToRemove, 1);
      setAllData([...allData]);
    }
    setEditing(false);
  }, [body, setAllData]));
  const updateData = useErrorHandling(useCallback(async () => {
    const payload = {
      name: body.name,
      notes: body.notes
    };
    const response = await generalRouteService.update(route, data.id, payload);
    setBody({
      ...response
    });
    setEditing(false);
  }, [body]));
  const renderNotes = editing ? /*#__PURE__*/React.createElement(Form.Control, {
    name: "notes",
    as: "textarea",
    maxLength: "300",
    style: {
      resize: 'none'
    },
    value: body.notes,
    onChange: changeBody
  }) : body.notes;
  const goToFolder = () => {
    if (editing || route !== 'folders') return;
    const isVirtualFolder = body.virtual_folder;
    if (isVirtualFolder) {
      goToFolderQuery(body.id, isVirtualFolder, body);
    } else if (route === 'folders') {
      setCurrentFolderId(body.id);
      goToFolderQuery(body.id);
    }
  };
  const renderNameOrLink = () => {
    if (Object.prototype.hasOwnProperty.call(body, 'download_link')) {
      return /*#__PURE__*/React.createElement("a", {
        href: body.download_link.url,
        target: "_blank",
        rel: "noreferrer"
      }, body.name);
    } else {
      return body.name;
    }
  };
  const pointerClass = route === 'folders' ? 'cursor-pointer' : '';
  const cursorMove = {
    cursor: 'move'
  };
  const renderColumn = columnName => {
    switch (columnName) {
      case 'folderIcon':
        return /*#__PURE__*/React.createElement("td", {
          onClick: () => goToFolder(),
          className: pointerClass
        }, /*#__PURE__*/React.createElement(FolderFileIcon, {
          isFile: Object.prototype.hasOwnProperty.call(body, 'notes'),
          contentType: body?.download_link?.content_type
        }));
      case 'createdAt':
        return /*#__PURE__*/React.createElement("td", {
          onClick: () => goToFolder(),
          className: pointerClass
        }, createTimezonedDate(body.created_at).toCustomLocaleDateString());
      case 'name':
        return /*#__PURE__*/React.createElement("td", {
          onClick: () => goToFolder(),
          className: pointerClass
        }, editing ? /*#__PURE__*/React.createElement(Form.Control, {
          name: "name",
          value: body.name,
          size: "md",
          type: "text",
          onChange: changeBody
        }) : renderNameOrLink());
      case 'notes':
        return /*#__PURE__*/React.createElement("td", {
          style: cursorMove
        }, Object.prototype.hasOwnProperty.call(body, 'notes') && renderNotes);
      case 'uploadedBy':
        return /*#__PURE__*/React.createElement("td", {
          style: cursorMove
        }, Object.prototype.hasOwnProperty.call(body, 'uploaded_by') && body.uploaded_by);
      case 'editButton':
        return /*#__PURE__*/React.createElement("td", null, !body.virtual_folder && showEditIfRestricted && canEditExistingDocumentsOrFolders && /*#__PURE__*/React.createElement(EditButtonsSet, {
          editing: editing,
          setEditing: setEditing,
          handleDelete: deleteData,
          handleUpdate: updateData,
          deleteProps: body.id,
          loading: loading
        }));
      default:
        return /*#__PURE__*/React.createElement("div", null, columnName, " does not exist");
    }
  };
  return FILE_MANAGEMENT_COLUMN_ORDER.map(columnName => renderColumn(columnName));
};
export default FoldersAndFilesRow;